import type { CalendarApi, EventApi } from "@fullcalendar/core"
import { hashQueryKey, type QueryClient, type QueryFunction, type QueryKey } from "@tanstack/react-query"

import type { TimelineCalendarEvent } from "../calendar/fullCalendarTypes"
import { withBatchRendering } from "../calendar/helpers"

// measured in Chrome with FullCalendar 6.1
const REFETCH_COST_RATIO = 200

const isCheaperToRefetch = (changes: number, totalEvents: number, sourceEvents: number) =>
    changes * totalEvents > REFETCH_COST_RATIO * sourceEvents

export type EventSourceEntry<TQueryKey extends QueryKey, TRaw> = {
    queryKey: TQueryKey
    // response currently on screen
    renderedRaw?: TRaw
    // response a refresh passes to the refetch it starts
    handoff?: TRaw
}

type SourceEventsPatch = Readonly<{
    remove: ReadonlyArray<EventApi>
    add: ReadonlyArray<TimelineCalendarEvent>
}>

export function takeHandoff<TQueryKey extends QueryKey, TRaw>(
    entry: EventSourceEntry<TQueryKey, TRaw> | undefined,
    queryKey: TQueryKey,
): TRaw | undefined {
    const handoff = entry?.handoff
    if (!entry || handoff === undefined || hashQueryKey(entry.queryKey) !== hashQueryKey(queryKey)) return undefined

    entry.handoff = undefined
    return handoff
}

export function memoizeByResponse<TRaw extends object>(
    toEvents: (raw: TRaw) => TimelineCalendarEvent[],
): (raw: TRaw) => TimelineCalendarEvent[] {
    const eventsByRaw = new WeakMap<TRaw, TimelineCalendarEvent[]>()

    return (raw) => {
        const cached = eventsByRaw.get(raw)
        if (cached) return cached

        const events = toEvents(raw)
        eventsByRaw.set(raw, events)
        return events
    }
}

// events need a signature (withEventSignature)
export function diffSourceEvents(
    mounted: ReadonlyArray<EventApi>,
    next: ReadonlyArray<TimelineCalendarEvent>,
): SourceEventsPatch {
    const mountedById = new Map(mounted.map((event) => [event.id, event]))
    const add: TimelineCalendarEvent[] = []

    for (const event of next) {
        const current = mountedById.get(event.id)
        if (current && current.extendedProps["signature"] === event.signature) {
            mountedById.delete(event.id)
        } else {
            add.push(event)
        }
    }

    return { remove: Array.from(mountedById.values()), add }
}

function groupBySource(events: ReadonlyArray<EventApi>): Map<string, EventApi[]> {
    const bySource = new Map<string, EventApi[]>()

    for (const event of events) {
        const sourceId = event.source?.id
        if (!sourceId) continue

        const sourceEvents = bySource.get(sourceId)
        if (sourceEvents) sourceEvents.push(event)
        else bySource.set(sourceId, [event])
    }

    return bySource
}

type RefreshEventSourcesArgs<
    TQueryKey extends QueryKey,
    TRaw,
    TEntry extends EventSourceEntry<TQueryKey, TRaw>,
> = Readonly<{
    calendar: CalendarApi
    queryClient: QueryClient
    // by source id; the fetcher replaces the entry on each fetch
    entries: Readonly<Record<string, TEntry>>
    queryFn: QueryFunction<TRaw, TQueryKey>
    toEvents: (entry: TEntry, raw: TRaw) => ReadonlyArray<TimelineCalendarEvent>
    shouldSkip?: () => boolean
}>

export async function refreshEventSources<
    TQueryKey extends QueryKey,
    TRaw,
    TEntry extends EventSourceEntry<TQueryKey, TRaw>,
>({
    calendar,
    queryClient,
    entries,
    queryFn,
    toEvents,
    shouldSkip,
}: RefreshEventSourcesArgs<TQueryKey, TRaw, TEntry>): Promise<void> {
    const sources = calendar.getEventSources().flatMap(({ id }) => {
        const entry = entries[id]
        return entry ? [{ sourceId: id, entry }] : []
    })

    const results = await Promise.allSettled(
        sources.map(async ({ sourceId, entry }) => ({
            sourceId,
            entry,
            raw: await queryClient.fetchQuery({ queryKey: entry.queryKey, queryFn, staleTime: 0 }),
        })),
    )

    if (shouldSkip?.()) return

    // structural sharing keeps an unchanged response identical
    const changed = results.flatMap((result) =>
        result.status === "fulfilled" &&
        entries[result.value.sourceId] === result.value.entry &&
        result.value.raw !== result.value.entry.renderedRaw
            ? [result.value]
            : [],
    )
    if (!changed.length) return

    const allEvents = calendar.getEvents()
    const mountedBySource = groupBySource(allEvents)
    const updates = changed.flatMap(({ sourceId, entry, raw }) => {
        // dropped meanwhile, e.g. by a location filter change
        const source = calendar.getEventSourceById(sourceId)
        if (!source) return []

        try {
            const mounted = mountedBySource.get(sourceId) ?? []
            const next = toEvents(entry, raw)
            const patch = diffSourceEvents(mounted, next)
            return [{ source, entry, raw, patch, sourceEvents: Math.max(mounted.length, next.length) }]
        } catch {
            // unreadable response: keep what is on screen
            return []
        }
    })

    // FullCalendar never resumes a batch that threw
    withBatchRendering(calendar, () => {
        updates.forEach(({ source, entry, raw, patch, sourceEvents }) => {
            entry.renderedRaw = raw

            if (isCheaperToRefetch(patch.remove.length + patch.add.length, allEvents.length, sourceEvents)) {
                entry.handoff = raw
                source.refetch()
                return
            }

            patch.remove.forEach((event) => event.remove())
            patch.add.forEach((event) => calendar.addEvent(event, source))
        })
    })
}
